/**
 * Contract of the hot translations (#4034): the shapes the translation management screens and routes share, and the
 * queries and data of its four reads. The four `…DataSchema` are what #4038 wraps with `freshness` in each screen:
 * `z.strictObject({ ...translationListDataSchema.shape, freshness: freshnessSchema })`. Nothing here declares a screen.
 *
 * People are `z.guid()` (the users seeded for e2e are not always RFC uuids), dates are ISO strings and `seq` is a
 * `number` (the service converts the `bigint` the database returns). Every object is strict.
 */

import { z } from 'zod/v4'

import { PLURAL_FORMS } from '../../i18n/plural-forms.js'
import {
  TRANSLATION_VALUE_ERROR_CODES,
  TRANSLATION_VALUE_MAX_LENGTH,
  TRANSLATION_VALUE_WARNING_CODES,
} from '../../i18n/validate-translation.js'
import { TRANSLATED_LOCALES } from '../../types/common.js'

export const TRANSLATION_AUDIENCES = ['customer', 'admin', 'server'] as const
export const TRANSLATION_ROW_STATUSES = [
  'catalogue',
  'overridden',
  'draft',
  'base-changed',
  'untranslated',
] as const
export const TRANSLATION_DRAFT_ACTIONS = ['set', 'remove'] as const
export const TRANSLATION_REVISION_ACTIONS = [
  'draft_saved',
  'draft_discarded',
  'removal_proposed',
  'published',
  'removed',
] as const
export const TRANSLATION_ERROR_CODES = [
  'TRANSLATION_VALUE_INVALID',
  'TRANSLATION_KEY_NOT_FOUND',
  'TRANSLATION_NOT_EDITABLE',
  'TRANSLATION_NO_CHANGE',
  'TRANSLATION_VERSION_CONFLICT',
  'TRANSLATION_DRAFTS_CHANGED',
  'TRANSLATION_WARNINGS_NOT_ACKNOWLEDGED',
  'TRANSLATION_NOTHING_TO_PUBLISH',
  'TRANSLATION_REVISION_NOT_FOUND',
  'TRANSLATION_PUBLICATION_NOT_FOUND',
  'TRANSLATION_TOO_MANY_DRAFTS',
  'TRANSLATION_CURSOR_INVALID',
] as const

export const TRANSLATION_LIST_DEFAULT_LIMIT = 50
export const TRANSLATION_LIST_MAX_LIMIT = 200
export const TRANSLATION_HISTORY_MAX = 100
export const TRANSLATION_PENDING_DRAFTS_MAX = 500
export const TRANSLATION_PUBLICATIONS_DEFAULT_LIMIT = 20
export const TRANSLATION_PUBLICATIONS_MAX_LIMIT = 100

export type TranslationAudience = (typeof TRANSLATION_AUDIENCES)[number]
export type TranslationRowStatus = (typeof TRANSLATION_ROW_STATUSES)[number]
export type TranslationDraftAction = (typeof TRANSLATION_DRAFT_ACTIONS)[number]
export type TranslationRevisionAction = (typeof TRANSLATION_REVISION_ACTIONS)[number]
export type TranslationErrorCode = (typeof TRANSLATION_ERROR_CODES)[number]

export const translationLocaleSchema = z.string().regex(/^[a-z]{2}$/)
export const translationReferenceSchema = z.enum(TRANSLATED_LOCALES)
export const translationNamespaceSchema = z.string().regex(/^[a-z][a-z0-9-]{0,63}$/)
export const translationKeySchema = z.string().regex(/^[A-Za-z0-9_./-]{1,300}$/)
export const translationCursorSchema = z.string().regex(/^[A-Za-z0-9_-]{1,512}$/)
export const translationVersionSchema = z.number().int().min(0)
export const translationValueSchema = z.string().max(TRANSLATION_VALUE_MAX_LENGTH)

/** One finding of `validateTranslationValue`: the same shape as its `TranslationIssue`. */
export const translationIssueSchema = z.strictObject({
  code: z.enum([...TRANSLATION_VALUE_ERROR_CODES, ...TRANSLATION_VALUE_WARNING_CODES]),
  placeholder: z.string().optional(),
  fragment: z.string().optional(),
  limit: z.number().int().optional(),
  expected: z.string().optional(),
  found: z.string().optional(),
})
export type TranslationIssueShape = z.infer<typeof translationIssueSchema>

export const translationRowSchema = z.strictObject({
  namespace: z.string(),
  key: z.string(),
  audience: z.enum(TRANSLATION_AUDIENCES),
  referenceValue: z.string(),
  localeBaseValue: z.string().nullable(),
  publishedOverride: z
    .strictObject({ value: z.string(), publishedAt: z.iso.datetime(), publicationId: z.guid() })
    .nullable(),
  draft: z
    .strictObject({
      action: z.enum(TRANSLATION_DRAFT_ACTIONS),
      value: z.string().nullable(),
      authorId: z.guid(),
      authorEmail: z.string().nullable(),
      updatedAt: z.iso.datetime(),
    })
    .nullable(),
  effectiveValue: z.string(),
  version: translationVersionSchema,
  statuses: z.array(z.enum(TRANSLATION_ROW_STATUSES)).min(1),
  baseChanged: z.boolean(),
  editable: z.boolean(),
  pluralSiblings: z.array(
    z.strictObject({ form: z.enum(PLURAL_FORMS), referenceValue: z.string() }),
  ),
})
export type TranslationRow = z.infer<typeof translationRowSchema>

export const translationRevisionSchema = z.strictObject({
  id: z.guid(),
  locale: translationLocaleSchema,
  namespace: z.string(),
  key: z.string(),
  action: z.enum(TRANSLATION_REVISION_ACTIONS),
  previousValue: z.string().nullable(),
  newValue: z.string().nullable(),
  publicationId: z.guid().nullable(),
  actorId: z.guid(),
  actorEmail: z.string().nullable(),
  createdAt: z.iso.datetime(),
})
export type TranslationRevision = z.infer<typeof translationRevisionSchema>

export const translationPublicationSchema = z.strictObject({
  id: z.guid(),
  seq: z.number().int().positive(),
  reason: z.string(),
  publishedById: z.guid(),
  publishedByEmail: z.string().nullable(),
  publishedAt: z.iso.datetime(),
  changeCount: z.number().int().positive(),
  locales: z.array(translationLocaleSchema).min(1),
  revertsPublicationId: z.guid().nullable(),
  contentSeq: z.number().int().positive().optional(),
})
export type TranslationPublication = z.infer<typeof translationPublicationSchema>

export const translationPendingDraftSchema = z.strictObject({
  locale: translationLocaleSchema,
  namespace: z.string(),
  key: z.string(),
  action: z.enum(TRANSLATION_DRAFT_ACTIONS),
  value: z.string().nullable(),
  referenceValue: z.string(),
  version: translationVersionSchema,
  warnings: z.array(translationIssueSchema),
})
export type TranslationPendingDraft = z.infer<typeof translationPendingDraftSchema>

const count = z.number().int().min(0)
const coverageOf = z.strictObject({ translated: count, total: count })

export const translationListQuerySchema = z.strictObject({
  locale: translationLocaleSchema,
  reference: translationReferenceSchema.default('en'),
  namespace: translationNamespaceSchema.optional(),
  audience: z.enum(TRANSLATION_AUDIENCES).optional(),
  status: z.enum(TRANSLATION_ROW_STATUSES).optional(),
  q: z
    .string()
    .trim()
    .min(1)
    .max(200)
    .refine((text) => !text.includes('\u0000'), 'q cannot contain a NUL character')
    .optional(),
  cursor: translationCursorSchema.optional(),
  limit: z.coerce
    .number<number>()
    .int()
    .min(1)
    .max(TRANSLATION_LIST_MAX_LIMIT)
    .default(TRANSLATION_LIST_DEFAULT_LIMIT),
})
export type TranslationListQuery = z.infer<typeof translationListQuerySchema>

export const translationListDataSchema = z.strictObject({
  page: z.strictObject({
    items: z.array(translationRowSchema),
    nextCursor: translationCursorSchema.nullable(),
  }),
  counts: z.strictObject({
    total: count,
    catalogue: count,
    overridden: count,
    draft: count,
    'base-changed': count,
    untranslated: count,
  }),
  coverage: z.strictObject({ customer: coverageOf, admin: coverageOf, server: coverageOf }),
  pendingDrafts: count,
})
export type TranslationListData = z.infer<typeof translationListDataSchema>

export const translationHistoryQuerySchema = z.strictObject({
  locale: translationLocaleSchema,
  namespace: translationNamespaceSchema,
  key: translationKeySchema,
})
export type TranslationHistoryQuery = z.infer<typeof translationHistoryQuerySchema>

export const translationHistoryDataSchema = z.strictObject({
  items: z.array(translationRevisionSchema).max(TRANSLATION_HISTORY_MAX),
  /** The real number of revisions of the key, so the screen can say the list is cut at `TRANSLATION_HISTORY_MAX`. */
  total: count,
})
export type TranslationHistoryData = z.infer<typeof translationHistoryDataSchema>

export const translationPendingDraftsQuerySchema = z.strictObject({
  locale: translationLocaleSchema.optional(),
})
export type TranslationPendingDraftsQuery = z.infer<typeof translationPendingDraftsQuerySchema>

export const translationPendingDraftsDataSchema = z.strictObject({
  items: z.array(translationPendingDraftSchema).max(TRANSLATION_PENDING_DRAFTS_MAX),
  total: count,
})
export type TranslationPendingDraftsData = z.infer<typeof translationPendingDraftsDataSchema>

export const translationPublicationsQuerySchema = z.strictObject({
  cursor: translationCursorSchema.optional(),
  limit: z.coerce
    .number<number>()
    .int()
    .min(1)
    .max(TRANSLATION_PUBLICATIONS_MAX_LIMIT)
    .default(TRANSLATION_PUBLICATIONS_DEFAULT_LIMIT),
})
export type TranslationPublicationsQuery = z.infer<typeof translationPublicationsQuerySchema>

export const translationPublicationsDataSchema = z.strictObject({
  page: z.strictObject({
    items: z.array(translationPublicationSchema),
    nextCursor: translationCursorSchema.nullable(),
  }),
})
export type TranslationPublicationsData = z.infer<typeof translationPublicationsDataSchema>
